import React, { useState, useEffect, useCallback, useRef } from 'react';

const API_BASE = 'https://imselling-marketplace-production.up.railway.app/api';
const getToken = () => { try { return localStorage.getItem('token') || localStorage.getItem('authToken') || ''; } catch { return ''; } };
const fmtKsh  = (v) => `KSh ${Number(v||0).toLocaleString('en-KE',{minimumFractionDigits:0})}`;
const fmtDate = (iso) => { if (!iso) return '—'; return new Date(iso).toLocaleString('en-KE',{day:'2-digit',month:'short',year:'numeric',hour:'2-digit',minute:'2-digit'}); };
const initials  = (s='') => (s||'?').slice(0,2).toUpperCase();
const nilDash   = (v) => (v==null||v===''||v==='—') ? null : v;
const AVATAR_COLORS = ['#6366f1','#0ea5e9','#10b981','#f59e0b','#ef4444','#8b5cf6','#ec4899','#14b8a6'];
const avatarColor = (s='') => { let h=0; for(const c of s) h=(h*31+c.charCodeAt(0))&0xffffffff; return AVATAR_COLORS[Math.abs(h)%AVATAR_COLORS.length]; };
const PLAN_META = { basic:{bg:'#fef3c7',color:'#b45309',border:'#fde68a',dot:'#f59e0b'}, pro:{bg:'#dbeafe',color:'#1d4ed8',border:'#bfdbfe',dot:'#3b82f6'}, business:{bg:'#f5f3ff',color:'#7c3aed',border:'#ddd6fe',dot:'#8b5cf6'} };
const planMeta  = (p='') => PLAN_META[(p||'').toLowerCase()] || {bg:'#f1f5f9',color:'#475569',border:'#e2e8f0',dot:'#94a3b8'};
const STATUS_META = { completed:{bg:'#dcfce7',color:'#16a34a',icon:'✅'}, pending:{bg:'#fef9c3',color:'#b45309',icon:'⏳'}, failed:{bg:'#fee2e2',color:'#dc2626',icon:'❌'}, cancelled:{bg:'#f1f5f9',color:'#64748b',icon:'⊘'} };
const statusMeta = (s='') => STATUS_META[s.toLowerCase()] || {bg:'#f8fafc',color:'#94a3b8',icon:'?'};
const PROMO_META = { free:{bg:'#f0fdf4',color:'#15803d',label:'Free Post'}, discounted:{bg:'#fef9c3',color:'#b45309',label:'Discounted'}, premium:{bg:'#f5f3ff',color:'#7c3aed',label:'Premium'}, repost:{bg:'#fff7ed',color:'#c2410c',label:'Re-Post'} };
const promoMeta = (t) => PROMO_META[t] || {bg:'#eef2ff',color:'#6366f1',label:t||'Promo'};

const Pill = ({bg,color,border,children,style={}}) => (<span style={{display:'inline-flex',alignItems:'center',gap:4,background:bg,color,border:`1px solid ${border||bg}`,borderRadius:20,padding:'2px 9px',fontSize:10.5,fontWeight:700,whiteSpace:'nowrap',...style}}>{children}</span>);
const Mono = ({children,color,size=11}) => (<span style={{fontFamily:'monospace',fontSize:size,color:color||'#1e293b',fontWeight:600}}>{children||'—'}</span>);
const Dash = () => (<span style={{color:'#cbd5e1',fontSize:13}}>—</span>);
const MediaCount = ({icon,count,activeColor}) => (<span style={{display:'inline-flex',alignItems:'center',gap:3}}><span style={{fontSize:13}}>{icon}</span><span style={{fontSize:12.5,fontWeight:700,color:count>0?activeColor:'#cbd5e1'}}>{count}</span></span>);

function SummaryCard({icon,label,value,sub,color,loading}){
  return(<div style={{flex:1,minWidth:160,background:'#fff',border:'1px solid #e2e8f0',borderRadius:14,padding:'18px 20px',borderTop:`3px solid ${color}`,display:'flex',alignItems:'center',gap:14}}>
    <div style={{width:46,height:46,borderRadius:12,background:`${color}15`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:22,flexShrink:0}}>{icon}</div>
    <div>
      <div style={{fontSize:10.5,color:'#94a3b8',fontWeight:700,textTransform:'uppercase',letterSpacing:'0.07em',marginBottom:3}}>{label}</div>
      <div style={{fontSize:21,fontWeight:800,color:'#0f172a',lineHeight:1}}>{loading?<span style={{display:'inline-block',width:70,height:18,background:'#f1f5f9',borderRadius:4}}/>:value}</div>
      {sub&&<div style={{fontSize:10.5,color:'#94a3b8',marginTop:3}}>{sub}</div>}
    </div>
  </div>);
}

// ✅ NEW: Global Commission Rate Editor
function GlobalCommissionEditor(){
  const [editing,setEditing]=useState(false);
  const [rate,setRate]=useState('30');
  const [loading,setLoading]=useState(true);
  const [saving,setSaving]=useState(false);
  const [msg,setMsg]=useState('');
  const ref=useRef(null);

  useEffect(()=>{
    const fetchRate=async()=>{
      try{
        const res=await fetch(`${API_BASE}/auth/admin/transactions/global-commission-rate/`,{headers:{Authorization:`Token ${getToken()}`}});
        const data=await res.json();
        if(res.ok)setRate(String(data.rate||30));
      }catch{setRate('30');}finally{setLoading(false);}
    };
    fetchRate();
  },[]);

  useEffect(()=>{if(editing)ref.current?.focus();},[editing]);

  const save=async()=>{
    const r=parseFloat(rate);
    if(isNaN(r)||r<0||r>100){setMsg('Rate must be 0-100');return;}
    setSaving(true);setMsg('');
    try{
      const res=await fetch(`${API_BASE}/auth/admin/transactions/global-commission-rate/`,{
        method:'POST',
        headers:{Authorization:`Token ${getToken()}`,'Content-Type':'application/json'},
        body:JSON.stringify({new_rate:r})
      });
      const data=await res.json();
      if(res.ok){
        setMsg(`✅ Updated to ${data.new_rate}%`);
        setTimeout(()=>{setMsg('');setEditing(false);},2000);
      } else {
        setMsg(`❌ ${data.error||'Error'}`);
      }
    }catch{setMsg('❌ Network error');}finally{setSaving(false);}
  };

  if(loading)return <span style={{fontSize:12,color:'#94a3b8'}}>Loading...</span>;

  return(<div style={{marginTop:8}}>
    {!editing?(
      <div style={{display:'flex',alignItems:'center',gap:8}}>
        <span style={{fontSize:13,fontWeight:700,color:'#10b981'}}>Default Commission: {rate}%</span>
        <button 
          onClick={()=>setEditing(true)} 
          style={{
            background:'linear-gradient(135deg,#6366f1,#8b5cf6)',
            color:'#fff',
            border:'none',
            borderRadius:6,
            padding:'4px 10px',
            fontSize:11,
            fontWeight:700,
            cursor:'pointer',
            display:'flex',
            alignItems:'center',
            gap:4
          }}
        >
          ✏️ Edit
        </button>
      </div>
    ):(
      <div style={{display:'flex',gap:6,alignItems:'center',flexWrap:'wrap'}}>
        <div style={{display:'flex',alignItems:'center',gap:4}}>
          <label style={{fontSize:11,fontWeight:700,color:'#6366f1'}}>New Rate:</label>
          <div style={{position:'relative'}}>
            <input 
              ref={ref}
              type="number" 
              min="0" 
              max="100" 
              step="0.5" 
              value={rate} 
              onChange={e=>setRate(e.target.value)}
              onKeyDown={e=>{
                if(e.key==='Enter')save();
                if(e.key==='Escape')setEditing(false);
              }}
              style={{
                width:70,
                height:30,
                padding:'0 22px 0 8px',
                border:'1.5px solid #6366f1',
                borderRadius:6,
                fontSize:12,
                fontWeight:700,
                outline:'none'
              }}
            />
            <span style={{position:'absolute',right:8,top:'50%',transform:'translateY(-50%)',fontSize:10,color:'#94a3b8',fontWeight:700}}>%</span>
          </div>
        </div>
        <button 
          onClick={save} 
          disabled={saving}
          style={{
            height:30,
            padding:'0 12px',
            background:saving?'#e2e8f0':'#10b981',
            color:saving?'#94a3b8':'#fff',
            border:'none',
            borderRadius:6,
            fontSize:11,
            fontWeight:700,
            cursor:saving?'not-allowed':'pointer',
            display:'flex',
            alignItems:'center',
            gap:4
          }}
        >
          {saving?'Saving...':'✓ Save'}
        </button>
        <button 
          onClick={()=>{setEditing(false);setMsg('');}}
          style={{
            height:30,
            padding:'0 10px',
            background:'#f1f5f9',
            color:'#64748b',
            border:'none',
            borderRadius:6,
            fontSize:11,
            fontWeight:700,
            cursor:'pointer'
          }}
        >
          ✕ Cancel
        </button>
      </div>
    )}
    {msg&&<div style={{fontSize:11,color:msg.startsWith('✅')?'#16a34a':'#dc2626',fontWeight:600,marginTop:4}}>{msg}</div>}
  </div>);
}

function CommissionEditor({row,onUpdated}){
  const [editing,setEditing]=useState(false);
  const [rate,setRate]=useState(String(row.commission_rate||30));
  const [saving,setSaving]=useState(false);
  const [msg,setMsg]=useState('');
  const ref=useRef(null);
  useEffect(()=>{if(editing)ref.current?.focus();},[editing]);
  if(!row.has_referral) return <Dash/>;
  const save=async()=>{
    const r=parseFloat(rate); if(isNaN(r)||r<0||r>100){setMsg('0-100');return;}
    setSaving(true);setMsg('');
    try{
      const res=await fetch(`${API_BASE}/auth/admin/transactions/commission/`,{method:'POST',headers:{Authorization:`Token ${getToken()}`,'Content-Type':'application/json'},body:JSON.stringify({commission_id:row.commission_id,new_rate:r})});
      const data=await res.json();
      if(res.ok){setMsg(`✅ KSh ${data.new_amount?.toFixed(0)}`);onUpdated?.({...row,commission_rate:data.new_rate,commission_amount:data.new_amount});setTimeout(()=>{setMsg('');setEditing(false);},1800);}
      else setMsg(`❌ ${data.error||'Error'}`);
    }catch{setMsg('❌ Network');}finally{setSaving(false);}
  };
  return(<div style={{display:'flex',flexDirection:'column',gap:3}}>
    {!editing?(<div style={{display:'flex',alignItems:'center',gap:5}}>
      <span style={{fontWeight:700,fontSize:12.5,color:'#10b981'}}>{fmtKsh(row.commission_amount)}</span>
      <span style={{fontSize:10,color:'#94a3b8'}}>({row.commission_rate}%)</span>
      <button onClick={()=>setEditing(true)} style={{background:'none',border:'1px solid #e2e8f0',borderRadius:5,cursor:'pointer',padding:'1px 5px',fontSize:9,color:'#6366f1',fontWeight:700}}>✏️</button>
    </div>):(<div style={{display:'flex',gap:4,alignItems:'center'}}>
      <div style={{position:'relative'}}>
        <input ref={ref} type="number" min="0" max="100" step="0.5" value={rate} onChange={e=>setRate(e.target.value)} onKeyDown={e=>{if(e.key==='Enter')save();if(e.key==='Escape')setEditing(false);}} style={{width:58,height:26,padding:'0 18px 0 6px',border:'1.5px solid #6366f1',borderRadius:6,fontSize:11,fontWeight:700,outline:'none'}}/>
        <span style={{position:'absolute',right:5,top:'50%',transform:'translateY(-50%)',fontSize:9,color:'#94a3b8'}}>%</span>
      </div>
      <button onClick={save} disabled={saving} style={{height:26,padding:'0 7px',background:saving?'#e2e8f0':'#6366f1',color:saving?'#94a3b8':'#fff',border:'none',borderRadius:5,fontSize:10,fontWeight:700,cursor:saving?'not-allowed':'pointer'}}>{saving?'…':'✓'}</button>
      <button onClick={()=>{setEditing(false);setMsg('');setRate(String(row.commission_rate||30));}} style={{height:26,padding:'0 6px',background:'#f1f5f9',border:'none',borderRadius:5,fontSize:10,cursor:'pointer'}}>✕</button>
    </div>)}
    {msg&&<span style={{fontSize:10,color:msg.startsWith('✅')?'#16a34a':'#dc2626',fontWeight:600}}>{msg}</span>}
  </div>);
}

function RowDetail({row,colCount}){
  const F=({label,value,accent,mono})=>(<div><div style={{fontSize:9.5,color:'#94a3b8',fontWeight:700,textTransform:'uppercase',letterSpacing:'0.06em',marginBottom:2}}>{label}</div><div style={{fontSize:12,color:accent||'#1e293b',fontWeight:accent?700:500,fontFamily:mono?'monospace':'inherit'}}>{value||'—'}</div></div>);
  const Section=({title,children})=>(<div style={{marginBottom:14}}><div style={{fontSize:10,fontWeight:800,color:'#6366f1',textTransform:'uppercase',letterSpacing:'0.08em',marginBottom:8,paddingBottom:4,borderBottom:'1px solid #e0e7ff'}}>{title}</div><div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(150px,1fr))',gap:'8px 20px'}}>{children}</div></div>);
  return(<tr><td colSpan={colCount} style={{padding:0}}>
    <div style={{background:'linear-gradient(135deg,#f8fafc,#f0f4ff)',borderLeft:'3px solid #6366f1',padding:'16px 24px'}}>
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))',gap:'0 32px'}}>
        <Section title="🧾 Transaction">
          <F label="Transaction Ref"     value={row.transaction_ref}     accent="#6366f1" mono/>
          <F label="M-Pesa Receipt"      value={row.mpesa_receipt}       accent={nilDash(row.mpesa_receipt)?'#16a34a':undefined} mono/>
          <F label="Checkout Request ID" value={row.checkout_request_id} mono/>
          <F label="Amount Paid" value={fmtKsh(row.amount)}/>
          <F label="Status"              value={row.status}/>
          <F label="Completed At"        value={fmtDate(row.completed_at)}/>
        </Section>
        <Section title="📦 Ad Details">
          <F label="Ad Name"      value={row.ad_name}/>
          <F label="Ad Code"      value={row.ad_code}      accent="#6366f1" mono/>
          <F label="Ad ID (raw)"  value={row.ad_id?`#${row.ad_id}`:'—'} mono/>
          <F label="Contact"      value={row.ad_contact}   accent="#0ea5e9" mono/>
          <F label="City"         value={row.ad_city}/>
          <F label="Images"       value={`${row.ad_image_count??0} image${row.ad_image_count!==1?'s':''}`}/>
          <F label="Videos"       value={`${row.ad_video_count??0} video${row.ad_video_count!==1?'s':''}`}/>
        </Section>
        <Section title="👤 Referee (Poster's Origin)">
          {row.poster_is_referee?<>
            <F label="Poster signed up via" value={row.poster_referred_by}      accent="#8b5cf6"/>
            <F label="Referred by (name)"   value={row.poster_referred_by_name}/>
          </>:<div style={{color:'#94a3b8',fontSize:12}}>Poster did not sign up via a referral link.</div>}
        </Section>
        <Section title="🔗 Referral Commission">
          {row.has_referral?<>
            <F label="Referrer (earned)"  value={row.referrer_email}    accent="#6366f1"/>
            <F label="Referrer Name"      value={row.referrer_name}/>
            <F label="Referee (paid)"     value={row.referee_email}     accent="#0ea5e9"/>
            <F label="Referee Name"       value={row.referee_name}/>
            <F label="Commission"         value={fmtKsh(row.commission_amount)} accent="#10b981"/>
            <F label="Rate"               value={`${row.commission_rate}%`}/>
            <F label="Status"             value={row.commission_status} accent={row.commission_status==='paid'?'#16a34a':'#b45309'}/>
            <F label="Commission ID"      value={row.commission_id?`#${row.commission_id}`:'—'} mono/>
          </>:<div style={{color:'#94a3b8',fontSize:12}}>No referral commission on this transaction.</div>}
        </Section>
        {row.is_promoted&&<Section title="🎁 Promotion">
          <F label="Type"  value={promoMeta(row.promotion_type).label} accent="#7c3aed"/>
          {row.promotion_notes&&<F label="Admin Notes" value={row.promotion_notes}/>}
        </Section>}
      </div>
    </div>
  </td></tr>);
}

function TransactionsTable(){
  const [rows,setRows]           = useState([]);
  const [summary,setSummary]     = useState(null);
  const [loading,setLoading]     = useState(true);
  const [error,setError]         = useState('');
  const [expandedId,setExpandedId]=useState(null);
  const [search,setSearch]       = useState('');
  const [statusF,setStatusF]     = useState('');
  const [planF,setPlanF]         = useState('');
  const [page,setPage]           = useState(1);
  const [totalPages,setTotalPages]=useState(1);
  const [totalCount,setTotalCount]=useState(0);
  
  // ✅ NEW: User-adjustable page size
  const [pageSize,setPageSize]   = useState(20);
  const [pageSizeInput,setPageSizeInput]=useState('20');
  
  const searchTimer=useRef(null);
  const [debouncedSearch,setDebouncedSearch]=useState('');

  useEffect(()=>{clearTimeout(searchTimer.current);searchTimer.current=setTimeout(()=>{setDebouncedSearch(search);setPage(1);},350);},[search]);

  const fetchData=useCallback(async()=>{
    setLoading(true);setError('');
    try{
      const params=new URLSearchParams({page,page_size:pageSize,...(statusF?{status:statusF}:{}),...(planF?{plan:planF}:{}),...(debouncedSearch?{search:debouncedSearch}:{})});
      const res=await fetch(`${API_BASE}/auth/admin/transactions/?${params}`,{headers:{Authorization:`Token ${getToken()}`}});
      const data=await res.json();
      if(!res.ok){setError(data.error||`Error ${res.status}`);return;}
      setRows(data.results||[]);setSummary(data.summary||null);setTotalPages(data.num_pages||1);setTotalCount(data.count||0);
    }catch(e){setError('Network error.');console.error(e);}finally{setLoading(false);}
  },[page,pageSize,statusF,planF,debouncedSearch]);

  useEffect(()=>{fetchData();},[fetchData]);
  const handleRowUpdate=(u)=>setRows(p=>p.map(r=>r.id===u.id?u:r));

  // ✅ NEW: Handle page size change
  const handlePageSizeChange=()=>{
    const size=parseInt(pageSizeInput,10);
    if(!isNaN(size)&&size>=1&&size<=100){
      setPageSize(size);
      setPage(1);
    }else{
      alert('Please enter a number between 1 and 100');
      setPageSizeInput(String(pageSize));
    }
  };

  const exportCSV=()=>{
    if(!rows.length)return;
    const h=['#','Txn Ref','Receipt','Status','Plan','Amount','Phone','Date','User Email','User Name','Ad Name','AD Code','Ad ID','Images','Videos','Contact','City','Has Referral Comm','Referrer','Referrer Name','Referee','Referee Name','Comm KSh','Comm Status','Poster Is Referee','Referred By','Referred By Name','Is Promoted','Promo Type','Promo Notes'];
    const csvRows=rows.map(r=>[r.id,r.transaction_ref,r.mpesa_receipt,r.status,r.plan,r.amount,r.phone,r.created_at?new Date(r.created_at).toLocaleString('en-KE'):'',r.user_email,r.user_name,r.ad_name,r.ad_code,r.ad_id,r.ad_image_count??0,r.ad_video_count??0,r.ad_contact||'',r.ad_city||'',r.has_referral?'Yes':'No',r.referrer_email||'',r.referrer_name||'',r.referee_email||'',r.referee_name||'',r.commission_amount,r.commission_status||'',r.poster_is_referee?'Yes':'No',r.poster_referred_by||'',r.poster_referred_by_name||'',r.is_promoted?'Yes':'No',r.promotion_type||'',r.promotion_notes||''].map(v=>`"${String(v??'').replace(/"/g,'""')}"`).join(','));
    const blob=new Blob(['\uFEFF'+[h.join(','),...csvRows].join('\n')],{type:'text/csv;charset=utf-8;'});
    const a=Object.assign(document.createElement('a'),{href:URL.createObjectURL(blob),download:`transactions_${new Date().toISOString().slice(0,10)}.csv`});
    a.click();URL.revokeObjectURL(a.href);
  };

  // 16 columns
  const COLS=[
    {key:'expand',label:'',w:32},{key:'id',label:'#',w:50},{key:'user',label:'User (Poster)',w:180},
    {key:'adname',label:'Ad Name',w:150},{key:'adcode',label:'AD ID / Code',w:105},
    {key:'media',label:'Media',w:75},{key:'contact',label:'Contact',w:120,isNew:true},
    {key:'plan',label:'Plan',w:88},{key:'amount',label:'Amount',w:108},{key:'status',label:'Status',w:85},
    {key:'txnref',label:'Txn Ref',w:128},{key:'referee',label:'Referee',w:155,isNew:true},
    {key:'referral',label:'Referral Comm',w:155},{key:'promo',label:'Promotion',w:105,isNew:true},
    {key:'commission',label:'Commission',w:138},{key:'date',label:'Date & Time',w:112},
  ];

  const pageNums=()=>{if(totalPages<=7)return Array.from({length:totalPages},(_,i)=>i+1);const p=page;if(p<=4)return[1,2,3,4,5,'…',totalPages];if(p>=totalPages-3)return[1,'…',totalPages-4,totalPages-3,totalPages-2,totalPages-1,totalPages];return[1,'…',p-1,p,p+1,'…',totalPages];};

  const TD={padding:'11px 13px',borderBottom:'1px solid #f1f5f9',verticalAlign:'middle'};
  const TH={padding:'10px 13px',background:'#f8fafc',color:'#64748b',fontSize:10.5,fontWeight:700,textTransform:'uppercase',letterSpacing:'0.07em',textAlign:'left',borderBottom:'2px solid #e2e8f0',whiteSpace:'nowrap',userSelect:'none'};

  return(
    <div style={{fontFamily:"'DM Sans','Inter',sans-serif"}}>

      {/* HEADER */}
      <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',marginBottom:20,flexWrap:'wrap',gap:12}}>
        <div style={{display:'flex',alignItems:'center',gap:12}}>
          <div style={{width:44,height:44,borderRadius:12,background:'linear-gradient(135deg,#6366f1,#8b5cf6)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:22,flexShrink:0}}>💳</div>
          <div>
            <h2 style={{margin:0,fontSize:20,fontWeight:800,color:'#0f172a',display:'flex',alignItems:'center',gap:8}}>
              Transaction Ledger
              {!loading&&<span style={{background:'#eef2ff',color:'#6366f1',fontSize:11,fontWeight:700,borderRadius:20,padding:'2px 10px'}}>{totalCount.toLocaleString()} records</span>}
            </h2>
            <p style={{margin:'2px 0 0',fontSize:11.5,color:'#94a3b8'}}>All M-Pesa payments · Ad details · Referee origin · Referral commissions · Promotions</p>
          </div>
        </div>
        <div style={{display:'flex',gap:8}}>
          <button onClick={fetchData} disabled={loading} style={{display:'flex',alignItems:'center',gap:6,padding:'9px 16px',background:'#eef2ff',color:'#6366f1',border:'1.5px solid #c7d2fe',borderRadius:9,fontSize:12.5,fontWeight:700,cursor:loading?'not-allowed':'pointer'}}>{loading?'⏳':'🔄'} Refresh</button>
          <button onClick={exportCSV} disabled={!rows.length} style={{display:'flex',alignItems:'center',gap:6,padding:'9px 18px',background:'#f0fdf4',color:'#15803d',border:'1.5px solid #bbf7d0',borderRadius:9,fontSize:12.5,fontWeight:700,cursor:rows.length?'pointer':'not-allowed'}}>📥 Export CSV</button>
        </div>
      </div>

      {/* SUMMARY + GLOBAL COMMISSION EDITOR */}
      <div style={{display:'flex',gap:12,marginBottom:22,flexWrap:'wrap'}}>
        <SummaryCard icon="💳" label="Transactions"    value={summary?.total_transactions?.toLocaleString()||'—'} color="#6366f1" loading={loading}/>
        <SummaryCard icon="✅" label="Completed"        value={summary?.completed?.toLocaleString()||'—'}           color="#10b981" loading={loading}/>
        <SummaryCard icon="💰" label="Total Revenue"    value={fmtKsh(summary?.total_revenue_kes)}                  color="#0ea5e9" loading={loading} sub="All successful payments"/>
        
        {/* ✅ NEW: Commission card with global editor */}
        <div style={{flex:1,minWidth:160,background:'#fff',border:'1px solid #e2e8f0',borderRadius:14,padding:'18px 20px',borderTop:'3px solid #f59e0b',display:'flex',flexDirection:'column',gap:8}}>
          <div style={{display:'flex',alignItems:'center',gap:14}}>
            <div style={{width:46,height:46,borderRadius:12,background:'#f59e0b15',display:'flex',alignItems:'center',justifyContent:'center',fontSize:22,flexShrink:0}}>🎁</div>
            <div>
              <div style={{fontSize:10.5,color:'#94a3b8',fontWeight:700,textTransform:'uppercase',letterSpacing:'0.07em',marginBottom:3}}>Commissions Paid</div>
              <div style={{fontSize:21,fontWeight:800,color:'#0f172a',lineHeight:1}}>{loading?<span style={{display:'inline-block',width:70,height:18,background:'#f1f5f9',borderRadius:4}}/>:fmtKsh(summary?.total_commissions_paid)}</div>
              <div style={{fontSize:10.5,color:'#94a3b8',marginTop:3}}>{fmtKsh(summary?.total_commissions_pending)} pending</div>
            </div>
          </div>
          <GlobalCommissionEditor/>
        </div>
      </div>

      {/* FILTERS + PAGE SIZE SELECTOR */}
      <div style={{display:'flex',gap:10,marginBottom:16,flexWrap:'wrap',alignItems:'center'}}>
        <div style={{flex:1,minWidth:220,position:'relative'}}>
          <svg style={{position:'absolute',left:12,top:'50%',transform:'translateY(-50%)',opacity:0.35}} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#475569" strokeWidth="2.5"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search email, receipt, ad name, code, contact…" style={{width:'100%',height:38,paddingLeft:36,paddingRight:36,border:'1px solid #e2e8f0',borderRadius:9,fontSize:12.5,background:'#f8fafc',outline:'none',boxSizing:'border-box',fontFamily:'inherit'}}/>
          {search&&<button onClick={()=>setSearch('')} style={{position:'absolute',right:10,top:'50%',transform:'translateY(-50%)',background:'none',border:'none',cursor:'pointer',fontSize:16,color:'#94a3b8',lineHeight:1}}>×</button>}
        </div>
        <select value={statusF} onChange={e=>{setStatusF(e.target.value);setPage(1);}} style={{height:38,padding:'0 10px',border:'1px solid #e2e8f0',borderRadius:9,fontSize:12.5,background:'#f8fafc',cursor:'pointer',color:'#475569',fontFamily:'inherit'}}>
          <option value="">All Statuses</option><option value="completed">✅ Completed</option><option value="pending">⏳ Pending</option><option value="failed">❌ Failed</option>
        </select>
        <select value={planF} onChange={e=>{setPlanF(e.target.value);setPage(1);}} style={{height:38,padding:'0 10px',border:'1px solid #e2e8f0',borderRadius:9,fontSize:12.5,background:'#f8fafc',cursor:'pointer',color:'#475569',fontFamily:'inherit'}}>
          <option value="">All Plans</option><option value="basic">🟡 Basic</option><option value="pro">🔵 Pro</option><option value="business">🟣 Business</option>
        </select>
        {(statusF||planF||debouncedSearch)&&<button onClick={()=>{setSearch('');setStatusF('');setPlanF('');setPage(1);}} style={{height:38,padding:'0 14px',background:'#fee2e2',color:'#dc2626',border:'1px solid #fca5a5',borderRadius:8,fontSize:12,fontWeight:700,cursor:'pointer'}}>✕ Clear</button>}
        
        {/* ✅ NEW: Rows per page selector */}
        <div style={{display:'flex',alignItems:'center',gap:6,marginLeft:'auto'}}>
          <label style={{fontSize:12,color:'#64748b',fontWeight:600,whiteSpace:'nowrap'}}>Rows per page:</label>
          <input 
            type="number" 
            min="1" 
            max="100" 
            value={pageSizeInput}
            onChange={e=>setPageSizeInput(e.target.value)}
            onBlur={handlePageSizeChange}
            onKeyDown={e=>{if(e.key==='Enter')handlePageSizeChange();}}
            style={{
              width:60,
              height:38,
              padding:'0 8px',
              border:'1.5px solid #6366f1',
              borderRadius:8,
              fontSize:13,
              fontWeight:700,
              textAlign:'center',
              outline:'none'
            }}
          />
        </div>
      </div>

      {/* ERROR */}
      {error&&<div style={{background:'#fee2e2',color:'#dc2626',border:'1px solid #fca5a5',borderRadius:9,padding:'11px 16px',marginBottom:14,fontSize:12.5,fontWeight:600,display:'flex',justifyContent:'space-between',alignItems:'center'}}><span>❌ {error}</span><button onClick={fetchData} style={{background:'none',border:'none',color:'#dc2626',cursor:'pointer',fontWeight:700,textDecoration:'underline'}}>Retry</button></div>}

      {/* TABLE */}
      <div style={{overflowX:'auto',border:'1px solid #e2e8f0',borderRadius:14,background:'#fff',boxShadow:'0 1px 4px #0001'}}>
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12.5}}>
          <thead>
            <tr>
              {COLS.map(col=>(
                <th key={col.key} style={{...TH,width:col.w}}>
                  {col.label}
                  {col.isNew&&<span style={{marginLeft:4,background:'#6366f1',color:'#fff',fontSize:8,fontWeight:800,borderRadius:10,padding:'1px 5px',verticalAlign:'middle'}}>NEW</span>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading?Array.from({length:7}).map((_,i)=>(
              <tr key={i} style={{background:i%2===0?'#fff':'#fafafa'}}>
                {COLS.map((col,j)=>(
                  <td key={j} style={TD}><div style={{height:13,background:'#f1f5f9',borderRadius:4,width:j===0?18:j===1?28:`${48+Math.sin(i*j)*28}%`}}/></td>
                ))}
              </tr>
            )):rows.length===0?(
              <tr><td colSpan={COLS.length} style={{padding:'60px 20px',textAlign:'center'}}>
                <div style={{fontSize:46,marginBottom:12}}>📭</div>
                <div style={{fontSize:14,fontWeight:700,color:'#475569',marginBottom:4}}>No transactions found</div>
                <div style={{fontSize:12.5,color:'#94a3b8'}}>{debouncedSearch||statusF||planF?'Try adjusting your filters.':'Transactions will appear here once payments are made.'}</div>
              </td></tr>
            ):rows.map((row,idx)=>{
              const isEx=expandedId===row.id;
              const pm=planMeta(row.plan);
              const sm=statusMeta(row.status);
              return(
                <React.Fragment key={row.id}>
                  <tr onClick={()=>setExpandedId(isEx?null:row.id)} style={{background:idx%2===0?'#fff':'#fafafa',cursor:'pointer',transition:'background 0.15s'}}>
                    
                    {/* Expand */}
                    <td style={TD}>
                      <button style={{background:'none',border:'none',cursor:'pointer',fontSize:15,lineHeight:1,color:'#94a3b8',padding:2,transition:'transform 0.2s',transform:isEx?'rotate(90deg)':'rotate(0)'}} onClick={e=>{e.stopPropagation();setExpandedId(isEx?null:row.id);}}>▶</button>
                    </td>

                    {/* ID */}
                    <td style={TD}>#{row.id}</td>

                    {/* User */}
                    <td style={TD}>
                      <div style={{display:'flex',alignItems:'center',gap:8}}>
                        <div style={{width:32,height:32,borderRadius:'50%',background:avatarColor(row.user_email||''),color:'#fff',fontSize:11.5,fontWeight:800,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>{initials(row.user_name||row.user_email||'?')}</div>
                        <div style={{overflow:'hidden'}}>
                          <div style={{fontSize:12,fontWeight:700,color:'#1e293b',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}} title={row.user_name||'—'}>{row.user_name||'—'}</div>
                          <div style={{fontSize:10.5,color:'#94a3b8',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}} title={row.user_email}>{row.user_email||'—'}</div>
                        </div>
                      </div>
                    </td>

                    {/* Ad Name */}
                    <td style={TD}>
                      <div style={{fontSize:12,fontWeight:700,color:'#1e293b',maxWidth:140,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}} title={row.ad_name}>{row.ad_name||'—'}</div>
                    </td>

                    {/* Ad Code */}
                    <td style={TD}>
                      <div>
                        <Mono color="#6366f1" size={10.5}>{row.ad_code||'—'}</Mono>
                        {row.ad_id&&<div style={{fontSize:9,color:'#cbd5e1',marginTop:1}}>id #{row.ad_id}</div>}
                      </div>
                    </td>

                    {/* Media */}
                    <td style={TD}>
                      <div style={{display:'flex',gap:9}}>
                        <MediaCount icon="📷" count={row.ad_image_count??0} activeColor="#0ea5e9"/>
                        <MediaCount icon="🎬" count={row.ad_video_count??0} activeColor="#ec4899"/>
                      </div>
                    </td>

                    {/* Contact */}
                    <td style={TD} onClick={e=>e.stopPropagation()}>
                      {nilDash(row.ad_contact)?(
                        <div>
                          <Mono color="#0ea5e9" size={11.5}>{row.ad_contact}</Mono>
                          {row.ad_city&&<div style={{fontSize:10,color:'#94a3b8',marginTop:1}}>📍 {row.ad_city}</div>}
                        </div>
                      ):<Dash/>}
                    </td>

                    {/* Plan */}
                    <td style={TD}>
                      <Pill bg={pm.bg} color={pm.color} border={pm.border}>
                        <span style={{width:6,height:6,borderRadius:'50%',background:pm.dot,display:'inline-block'}}/>
                        {(row.plan||'—').toUpperCase()}
                      </Pill>
                    </td>

                    {/* Amount */}
                    <td style={TD}>
                      <div style={{fontWeight:800,fontSize:12.5,color:'#0f172a'}}>{fmtKsh(row.amount)}</div>
                    </td>

                    {/* Status */}
                    <td style={TD}><Pill bg={sm.bg} color={sm.color}>{sm.icon} {row.status}</Pill></td>

                    {/* Txn Ref */}
                    <td style={TD} onClick={e=>e.stopPropagation()}>
                      <span title={row.transaction_ref} style={{display:'block',fontSize:10.5,fontWeight:700,color:'#0f172a',fontFamily:'monospace',maxWidth:118,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',cursor:'text',userSelect:'all'}}>{row.transaction_ref||'—'}</span>
                    </td>

                    {/* Referee — was the POSTER themselves referred by someone? */}
                    <td style={TD}>
                      {row.poster_is_referee?(
                        <div>
                          <div style={{fontSize:9.5,color:'#7c3aed',fontWeight:800,marginBottom:1,textTransform:'uppercase',letterSpacing:'0.05em'}}>via referral</div>
                          <div style={{fontSize:10.5,fontWeight:700,color:'#6366f1',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',maxWidth:140}} title={row.poster_referred_by}>← {row.poster_referred_by}</div>
                          {row.poster_referred_by_name&&<div style={{fontSize:10,color:'#94a3b8',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',maxWidth:140}}>{row.poster_referred_by_name}</div>}
                        </div>
                      ):<Dash/>}
                    </td>

                    {/* Referral Commission */}
                    <td style={TD}>
                      {row.has_referral?(
                        <div>
                          <div style={{fontSize:10.5,fontWeight:700,color:'#6366f1',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',maxWidth:140}} title={row.referrer_email}>↑ {row.referrer_email}</div>
                          <div style={{fontSize:10,color:'#94a3b8',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',maxWidth:140}} title={row.referee_email}>↓ {row.referee_email}</div>
                        </div>
                      ):<Dash/>}
                    </td>

                    {/* Promotion */}
                    <td style={TD}>
                      {row.is_promoted?<Pill bg={promoMeta(row.promotion_type).bg} color={promoMeta(row.promotion_type).color}>🎁 {promoMeta(row.promotion_type).label}</Pill>:<Dash/>}
                    </td>

                    {/* Commission editor */}
                    <td style={TD} onClick={e=>e.stopPropagation()}>
                      <CommissionEditor row={row} onUpdated={handleRowUpdate}/>
                    </td>

                    {/* Date */}
                    <td style={TD}>
                      <div style={{fontSize:11.5,color:'#475569',whiteSpace:'nowrap'}}>{row.created_at?new Date(row.created_at).toLocaleDateString('en-KE',{day:'2-digit',month:'short',year:'numeric'}):'—'}</div>
                      <div style={{fontSize:10,color:'#94a3b8',marginTop:1,whiteSpace:'nowrap'}}>{row.created_at?new Date(row.created_at).toLocaleTimeString('en-KE',{hour:'2-digit',minute:'2-digit'}):''}</div>
                    </td>

                  </tr>
                  {isEx&&<RowDetail row={row} colCount={COLS.length}/>}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* PAGINATION */}
      {!loading&&totalPages>1&&(
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginTop:16,flexWrap:'wrap',gap:8}}>
          <span style={{fontSize:12,color:'#94a3b8'}}>Showing {((page-1)*pageSize)+1}–{Math.min(page*pageSize,totalCount)} of {totalCount.toLocaleString()}</span>
          <div style={{display:'flex',gap:4,alignItems:'center'}}>
            <button onClick={()=>setPage(p=>Math.max(1,p-1))} disabled={page===1} style={{width:32,height:32,border:'1px solid #e2e8f0',borderRadius:7,background:'#fff',color:'#475569',fontSize:13,fontWeight:700,cursor:page===1?'not-allowed':'pointer'}}>‹</button>
            {pageNums().map((n,i)=>n==='…'?<span key={`e${i}`} style={{padding:'0 4px',color:'#94a3b8',fontSize:13}}>…</span>:<button key={n} onClick={()=>setPage(n)} style={{width:32,height:32,border:`1px solid ${page===n?'#6366f1':'#e2e8f0'}`,borderRadius:7,background:page===n?'#6366f1':'#fff',color:page===n?'#fff':'#475569',fontSize:12.5,fontWeight:700,cursor:page===n?'default':'pointer'}}>{n}</button>)}
            <button onClick={()=>setPage(p=>Math.min(totalPages,p+1))} disabled={page===totalPages} style={{width:32,height:32,border:'1px solid #e2e8f0',borderRadius:7,background:'#fff',color:'#475569',fontSize:13,fontWeight:700,cursor:page===totalPages?'not-allowed':'pointer'}}>›</button>
          </div>
        </div>
      )}

    </div>
  );
}

export default TransactionsTable;




























// import React, { useState, useEffect, useCallback, useRef } from 'react';

// const API_BASE = 'https://imselling-marketplace-production.up.railway.app/api';
// const getToken = () => { try { return localStorage.getItem('token') || localStorage.getItem('authToken') || ''; } catch { return ''; } };
// const fmtKsh  = (v) => `KSh ${Number(v||0).toLocaleString('en-KE',{minimumFractionDigits:0})}`;
// const fmtDate = (iso) => { if (!iso) return '—'; return new Date(iso).toLocaleString('en-KE',{day:'2-digit',month:'short',year:'numeric',hour:'2-digit',minute:'2-digit'}); };
// const initials  = (s='') => (s||'?').slice(0,2).toUpperCase();
// const nilDash   = (v) => (v==null||v===''||v==='—') ? null : v;
// const AVATAR_COLORS = ['#6366f1','#0ea5e9','#10b981','#f59e0b','#ef4444','#8b5cf6','#ec4899','#14b8a6'];
// const avatarColor = (s='') => { let h=0; for(const c of s) h=(h*31+c.charCodeAt(0))&0xffffffff; return AVATAR_COLORS[Math.abs(h)%AVATAR_COLORS.length]; };
// const PLAN_META = { basic:{bg:'#fef3c7',color:'#b45309',border:'#fde68a',dot:'#f59e0b'}, pro:{bg:'#dbeafe',color:'#1d4ed8',border:'#bfdbfe',dot:'#3b82f6'}, business:{bg:'#f5f3ff',color:'#7c3aed',border:'#ddd6fe',dot:'#8b5cf6'} };
// const planMeta  = (p='') => PLAN_META[(p||'').toLowerCase()] || {bg:'#f1f5f9',color:'#475569',border:'#e2e8f0',dot:'#94a3b8'};
// const STATUS_META = { completed:{bg:'#dcfce7',color:'#16a34a',icon:'✅'}, pending:{bg:'#fef9c3',color:'#b45309',icon:'⏳'}, failed:{bg:'#fee2e2',color:'#dc2626',icon:'❌'}, cancelled:{bg:'#f1f5f9',color:'#64748b',icon:'⊘'} };
// const statusMeta = (s='') => STATUS_META[s.toLowerCase()] || {bg:'#f8fafc',color:'#94a3b8',icon:'?'};
// const PROMO_META = { free:{bg:'#f0fdf4',color:'#15803d',label:'Free Post'}, discounted:{bg:'#fef9c3',color:'#b45309',label:'Discounted'}, premium:{bg:'#f5f3ff',color:'#7c3aed',label:'Premium'}, repost:{bg:'#fff7ed',color:'#c2410c',label:'Re-Post'} };
// const promoMeta = (t) => PROMO_META[t] || {bg:'#eef2ff',color:'#6366f1',label:t||'Promo'};

// const Pill = ({bg,color,border,children,style={}}) => (<span style={{display:'inline-flex',alignItems:'center',gap:4,background:bg,color,border:`1px solid ${border||bg}`,borderRadius:20,padding:'2px 9px',fontSize:10.5,fontWeight:700,whiteSpace:'nowrap',...style}}>{children}</span>);
// const Mono = ({children,color,size=11}) => (<span style={{fontFamily:'monospace',fontSize:size,color:color||'#1e293b',fontWeight:600}}>{children||'—'}</span>);
// const Dash = () => (<span style={{color:'#cbd5e1',fontSize:13}}>—</span>);
// const MediaCount = ({icon,count,activeColor}) => (<span style={{display:'inline-flex',alignItems:'center',gap:3}}><span style={{fontSize:13}}>{icon}</span><span style={{fontSize:12.5,fontWeight:700,color:count>0?activeColor:'#cbd5e1'}}>{count}</span></span>);

// function SummaryCard({icon,label,value,sub,color,loading}){
//   return(<div style={{flex:1,minWidth:160,background:'#fff',border:'1px solid #e2e8f0',borderRadius:14,padding:'18px 20px',borderTop:`3px solid ${color}`,display:'flex',alignItems:'center',gap:14}}>
//     <div style={{width:46,height:46,borderRadius:12,background:`${color}15`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:22,flexShrink:0}}>{icon}</div>
//     <div>
//       <div style={{fontSize:10.5,color:'#94a3b8',fontWeight:700,textTransform:'uppercase',letterSpacing:'0.07em',marginBottom:3}}>{label}</div>
//       <div style={{fontSize:21,fontWeight:800,color:'#0f172a',lineHeight:1}}>{loading?<span style={{display:'inline-block',width:70,height:18,background:'#f1f5f9',borderRadius:4}}/>:value}</div>
//       {sub&&<div style={{fontSize:10.5,color:'#94a3b8',marginTop:3}}>{sub}</div>}
//     </div>
//   </div>);
// }

// function CommissionEditor({row,onUpdated}){
//   const [editing,setEditing]=useState(false);
//   const [rate,setRate]=useState(String(row.commission_rate||30));
//   const [saving,setSaving]=useState(false);
//   const [msg,setMsg]=useState('');
//   const ref=useRef(null);
//   useEffect(()=>{if(editing)ref.current?.focus();},[editing]);
//   if(!row.has_referral) return <Dash/>;
//   const save=async()=>{
//     const r=parseFloat(rate); if(isNaN(r)||r<0||r>100){setMsg('0-100');return;}
//     setSaving(true);setMsg('');
//     try{
//       const res=await fetch(`${API_BASE}/auth/admin/transactions/commission/`,{method:'POST',headers:{Authorization:`Token ${getToken()}`,'Content-Type':'application/json'},body:JSON.stringify({commission_id:row.commission_id,new_rate:r})});
//       const data=await res.json();
//       if(res.ok){setMsg(`✅ KSh ${data.new_amount?.toFixed(0)}`);onUpdated?.({...row,commission_rate:data.new_rate,commission_amount:data.new_amount});setTimeout(()=>{setMsg('');setEditing(false);},1800);}
//       else setMsg(`❌ ${data.error||'Error'}`);
//     }catch{setMsg('❌ Network');}finally{setSaving(false);}
//   };
//   return(<div style={{display:'flex',flexDirection:'column',gap:3}}>
//     {!editing?(<div style={{display:'flex',alignItems:'center',gap:5}}>
//       <span style={{fontWeight:700,fontSize:12.5,color:'#10b981'}}>{fmtKsh(row.commission_amount)}</span>
//       <span style={{fontSize:10,color:'#94a3b8'}}>({row.commission_rate}%)</span>
//       <button onClick={()=>setEditing(true)} style={{background:'none',border:'1px solid #e2e8f0',borderRadius:5,cursor:'pointer',padding:'1px 5px',fontSize:9,color:'#6366f1',fontWeight:700}}>✏️</button>
//     </div>):(<div style={{display:'flex',gap:4,alignItems:'center'}}>
//       <div style={{position:'relative'}}>
//         <input ref={ref} type="number" min="0" max="100" step="0.5" value={rate} onChange={e=>setRate(e.target.value)} onKeyDown={e=>{if(e.key==='Enter')save();if(e.key==='Escape')setEditing(false);}} style={{width:58,height:26,padding:'0 18px 0 6px',border:'1.5px solid #6366f1',borderRadius:6,fontSize:11,fontWeight:700,outline:'none'}}/>
//         <span style={{position:'absolute',right:5,top:'50%',transform:'translateY(-50%)',fontSize:9,color:'#94a3b8'}}>%</span>
//       </div>
//       <button onClick={save} disabled={saving} style={{height:26,padding:'0 7px',background:saving?'#e2e8f0':'#6366f1',color:saving?'#94a3b8':'#fff',border:'none',borderRadius:5,fontSize:10,fontWeight:700,cursor:saving?'not-allowed':'pointer'}}>{saving?'…':'✓'}</button>
//       <button onClick={()=>{setEditing(false);setMsg('');setRate(String(row.commission_rate||30));}} style={{height:26,padding:'0 6px',background:'#f1f5f9',border:'none',borderRadius:5,fontSize:10,cursor:'pointer'}}>✕</button>
//     </div>)}
//     {msg&&<span style={{fontSize:10,color:msg.startsWith('✅')?'#16a34a':'#dc2626',fontWeight:600}}>{msg}</span>}
//   </div>);
// }

// function RowDetail({row,colCount}){
//   const F=({label,value,accent,mono})=>(<div><div style={{fontSize:9.5,color:'#94a3b8',fontWeight:700,textTransform:'uppercase',letterSpacing:'0.06em',marginBottom:2}}>{label}</div><div style={{fontSize:12,color:accent||'#1e293b',fontWeight:accent?700:500,fontFamily:mono?'monospace':'inherit'}}>{value||'—'}</div></div>);
//   const Section=({title,children})=>(<div style={{marginBottom:14}}><div style={{fontSize:10,fontWeight:800,color:'#6366f1',textTransform:'uppercase',letterSpacing:'0.08em',marginBottom:8,paddingBottom:4,borderBottom:'1px solid #e0e7ff'}}>{title}</div><div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(150px,1fr))',gap:'8px 20px'}}>{children}</div></div>);
//   return(<tr><td colSpan={colCount} style={{padding:0}}>
//     <div style={{background:'linear-gradient(135deg,#f8fafc,#f0f4ff)',borderLeft:'3px solid #6366f1',padding:'16px 24px'}}>
//       <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))',gap:'0 32px'}}>
//         <Section title="🧾 Transaction">
//           <F label="Transaction Ref"     value={row.transaction_ref}     accent="#6366f1" mono/>
//           <F label="M-Pesa Receipt"      value={row.mpesa_receipt}       accent={nilDash(row.mpesa_receipt)?'#16a34a':undefined} mono/>
//           <F label="Checkout Request ID" value={row.checkout_request_id} mono/>
//           <F label="Amount Paid"         value={fmtKsh(row.amount)}      accent={row.is_discounted?'#d97706':undefined}/>
//           <F label="Full Plan Price"     value={fmtKsh(row.full_plan_price)}/>
//           {row.is_discounted&&<F label="Discount" value={`${row.discount_pct}% off`} accent="#d97706"/>}
//           <F label="Status"              value={row.status}/>
//           <F label="Completed At"        value={fmtDate(row.completed_at)}/>
//         </Section>
//         <Section title="📦 Ad Details">
//           <F label="Ad Name"      value={row.ad_name}/>
//           <F label="Ad Code"      value={row.ad_code}      accent="#6366f1" mono/>
//           <F label="Ad ID (raw)"  value={row.ad_id?`#${row.ad_id}`:'—'} mono/>
//           <F label="Contact"      value={row.ad_contact}   accent="#0ea5e9" mono/>
//           <F label="City"         value={row.ad_city}/>
//           <F label="Images"       value={`${row.ad_image_count??0} image${row.ad_image_count!==1?'s':''}`}/>
//           <F label="Videos"       value={`${row.ad_video_count??0} video${row.ad_video_count!==1?'s':''}`}/>
//         </Section>
//         <Section title="👤 Referee (Poster's Origin)">
//           {row.poster_is_referee?<>
//             <F label="Poster signed up via" value={row.poster_referred_by}      accent="#8b5cf6"/>
//             <F label="Referred by (name)"   value={row.poster_referred_by_name}/>
//           </>:<div style={{color:'#94a3b8',fontSize:12}}>Poster did not sign up via a referral link.</div>}
//         </Section>
//         <Section title="🔗 Referral Commission">
//           {row.has_referral?<>
//             <F label="Referrer (earned)"  value={row.referrer_email}    accent="#6366f1"/>
//             <F label="Referrer Name"      value={row.referrer_name}/>
//             <F label="Referee (paid)"     value={row.referee_email}     accent="#0ea5e9"/>
//             <F label="Referee Name"       value={row.referee_name}/>
//             <F label="Commission"         value={fmtKsh(row.commission_amount)} accent="#10b981"/>
//             <F label="Rate"               value={`${row.commission_rate}%`}/>
//             <F label="Status"             value={row.commission_status} accent={row.commission_status==='paid'?'#16a34a':'#b45309'}/>
//             <F label="Commission ID"      value={row.commission_id?`#${row.commission_id}`:'—'} mono/>
//           </>:<div style={{color:'#94a3b8',fontSize:12}}>No referral commission on this transaction.</div>}
//         </Section>
//         {row.is_promoted&&<Section title="🎁 Promotion">
//           <F label="Type"  value={promoMeta(row.promotion_type).label} accent="#7c3aed"/>
//           {row.promotion_notes&&<F label="Admin Notes" value={row.promotion_notes}/>}
//         </Section>}
//       </div>
//     </div>
//   </td></tr>);
// }

// function TransactionsTable(){
//   const [rows,setRows]           = useState([]);
//   const [summary,setSummary]     = useState(null);
//   const [loading,setLoading]     = useState(true);
//   const [error,setError]         = useState('');
//   const [expandedId,setExpandedId]=useState(null);
//   const [search,setSearch]       = useState('');
//   const [statusF,setStatusF]     = useState('');
//   const [planF,setPlanF]         = useState('');
//   const [page,setPage]           = useState(1);
//   const [totalPages,setTotalPages]=useState(1);
//   const [totalCount,setTotalCount]=useState(0);
//   const PAGE_SIZE=20;
//   const searchTimer=useRef(null);
//   const [debouncedSearch,setDebouncedSearch]=useState('');

//   useEffect(()=>{clearTimeout(searchTimer.current);searchTimer.current=setTimeout(()=>{setDebouncedSearch(search);setPage(1);},350);},[search]);

//   const fetchData=useCallback(async()=>{
//     setLoading(true);setError('');
//     try{
//       const params=new URLSearchParams({page,page_size:PAGE_SIZE,...(statusF?{status:statusF}:{}),...(planF?{plan:planF}:{}),...(debouncedSearch?{search:debouncedSearch}:{})});
//       const res=await fetch(`${API_BASE}/auth/admin/transactions/?${params}`,{headers:{Authorization:`Token ${getToken()}`}});
//       const data=await res.json();
//       if(!res.ok){setError(data.error||`Error ${res.status}`);return;}
//       setRows(data.results||[]);setSummary(data.summary||null);setTotalPages(data.num_pages||1);setTotalCount(data.count||0);
//     }catch(e){setError('Network error.');console.error(e);}finally{setLoading(false);}
//   },[page,statusF,planF,debouncedSearch]);

//   useEffect(()=>{fetchData();},[fetchData]);
//   const handleRowUpdate=(u)=>setRows(p=>p.map(r=>r.id===u.id?u:r));

//   const exportCSV=()=>{
//     if(!rows.length)return;
//     const h=['#','Txn Ref','Receipt','Status','Plan','Amount','Full Price','Discounted','Discount%','Phone','Date','User Email','User Name','Ad Name','AD Code','Ad ID','Images','Videos','Contact','City','Has Referral Comm','Referrer','Referrer Name','Referee','Referee Name','Comm KSh','Comm%','Comm Status','Poster Is Referee','Referred By','Referred By Name','Is Promoted','Promo Type','Promo Notes'];
//     const csvRows=rows.map(r=>[r.id,r.transaction_ref,r.mpesa_receipt,r.status,r.plan,r.amount,r.full_plan_price,r.is_discounted?'Yes':'No',r.discount_pct,r.phone,r.created_at?new Date(r.created_at).toLocaleString('en-KE'):'',r.user_email,r.user_name,r.ad_name,r.ad_code,r.ad_id,r.ad_image_count??0,r.ad_video_count??0,r.ad_contact||'',r.ad_city||'',r.has_referral?'Yes':'No',r.referrer_email||'',r.referrer_name||'',r.referee_email||'',r.referee_name||'',r.commission_amount,r.commission_rate,r.commission_status||'',r.poster_is_referee?'Yes':'No',r.poster_referred_by||'',r.poster_referred_by_name||'',r.is_promoted?'Yes':'No',r.promotion_type||'',r.promotion_notes||''].map(v=>`"${String(v??'').replace(/"/g,'""')}"`).join(','));
//     const blob=new Blob(['\uFEFF'+[h.join(','),...csvRows].join('\n')],{type:'text/csv;charset=utf-8;'});
//     const a=Object.assign(document.createElement('a'),{href:URL.createObjectURL(blob),download:`transactions_${new Date().toISOString().slice(0,10)}.csv`});
//     a.click();URL.revokeObjectURL(a.href);
//   };

//   // 16 columns
//   const COLS=[
//     {key:'expand',label:'',w:32},{key:'id',label:'#',w:50},{key:'user',label:'User (Poster)',w:180},
//     {key:'adname',label:'Ad Name',w:150},{key:'adcode',label:'AD ID / Code',w:105},
//     {key:'media',label:'Media',w:75},{key:'contact',label:'Contact',w:120,isNew:true},
//     {key:'plan',label:'Plan',w:88},{key:'amount',label:'Amount',w:108},{key:'status',label:'Status',w:85},
//     {key:'txnref',label:'Txn Ref',w:128},{key:'referee',label:'Referee',w:155,isNew:true},
//     {key:'referral',label:'Referral Comm',w:155},{key:'promo',label:'Promotion',w:105,isNew:true},
//     {key:'commission',label:'Commission',w:138},{key:'date',label:'Date & Time',w:112},
//   ];

//   const pageNums=()=>{if(totalPages<=7)return Array.from({length:totalPages},(_,i)=>i+1);const p=page;if(p<=4)return[1,2,3,4,5,'…',totalPages];if(p>=totalPages-3)return[1,'…',totalPages-4,totalPages-3,totalPages-2,totalPages-1,totalPages];return[1,'…',p-1,p,p+1,'…',totalPages];};

//   const TD={padding:'11px 13px',borderBottom:'1px solid #f1f5f9',verticalAlign:'middle'};
//   const TH={padding:'10px 13px',background:'#f8fafc',color:'#64748b',fontSize:10.5,fontWeight:700,textTransform:'uppercase',letterSpacing:'0.07em',textAlign:'left',borderBottom:'2px solid #e2e8f0',whiteSpace:'nowrap',userSelect:'none'};

//   return(
//     <div style={{fontFamily:"'DM Sans','Inter',sans-serif"}}>

//       {/* HEADER */}
//       <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',marginBottom:20,flexWrap:'wrap',gap:12}}>
//         <div style={{display:'flex',alignItems:'center',gap:12}}>
//           <div style={{width:44,height:44,borderRadius:12,background:'linear-gradient(135deg,#6366f1,#8b5cf6)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:22,flexShrink:0}}>💳</div>
//           <div>
//             <h2 style={{margin:0,fontSize:20,fontWeight:800,color:'#0f172a',display:'flex',alignItems:'center',gap:8}}>
//               Transaction Ledger
//               {!loading&&<span style={{background:'#eef2ff',color:'#6366f1',fontSize:11,fontWeight:700,borderRadius:20,padding:'2px 10px'}}>{totalCount.toLocaleString()} records</span>}
//             </h2>
//             <p style={{margin:'2px 0 0',fontSize:11.5,color:'#94a3b8'}}>All M-Pesa payments · Ad details · Referee origin · Referral commissions · Promotions</p>
//           </div>
//         </div>
//         <div style={{display:'flex',gap:8}}>
//           <button onClick={fetchData} disabled={loading} style={{display:'flex',alignItems:'center',gap:6,padding:'9px 16px',background:'#eef2ff',color:'#6366f1',border:'1.5px solid #c7d2fe',borderRadius:9,fontSize:12.5,fontWeight:700,cursor:loading?'not-allowed':'pointer'}}>{loading?'⏳':'🔄'} Refresh</button>
//           <button onClick={exportCSV} disabled={!rows.length} style={{display:'flex',alignItems:'center',gap:6,padding:'9px 18px',background:'#f0fdf4',color:'#15803d',border:'1.5px solid #bbf7d0',borderRadius:9,fontSize:12.5,fontWeight:700,cursor:rows.length?'pointer':'not-allowed'}}>📥 Export CSV</button>
//         </div>
//       </div>

//       {/* SUMMARY */}
//       <div style={{display:'flex',gap:12,marginBottom:22,flexWrap:'wrap'}}>
//         <SummaryCard icon="💳" label="Transactions"    value={summary?.total_transactions?.toLocaleString()||'—'} color="#6366f1" loading={loading}/>
//         <SummaryCard icon="✅" label="Completed"        value={summary?.completed?.toLocaleString()||'—'}           color="#10b981" loading={loading}/>
//         <SummaryCard icon="💰" label="Total Revenue"    value={fmtKsh(summary?.total_revenue_kes)}                  color="#0ea5e9" loading={loading} sub="All successful payments"/>
//         <SummaryCard icon="🎁" label="Commissions Paid" value={fmtKsh(summary?.total_commissions_paid)}            color="#f59e0b" loading={loading} sub={`${fmtKsh(summary?.total_commissions_pending)} pending`}/>
//       </div>

//       {/* FILTERS */}
//       <div style={{display:'flex',gap:10,marginBottom:16,flexWrap:'wrap',alignItems:'center'}}>
//         <div style={{flex:1,minWidth:220,position:'relative'}}>
//           <svg style={{position:'absolute',left:12,top:'50%',transform:'translateY(-50%)',opacity:0.35}} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#475569" strokeWidth="2.5"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
//           <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search email, receipt, ad name, code, contact…" style={{width:'100%',height:38,paddingLeft:36,paddingRight:36,border:'1px solid #e2e8f0',borderRadius:9,fontSize:12.5,background:'#f8fafc',outline:'none',boxSizing:'border-box',fontFamily:'inherit'}}/>
//           {search&&<button onClick={()=>setSearch('')} style={{position:'absolute',right:10,top:'50%',transform:'translateY(-50%)',background:'none',border:'none',cursor:'pointer',fontSize:16,color:'#94a3b8',lineHeight:1}}>×</button>}
//         </div>
//         <select value={statusF} onChange={e=>{setStatusF(e.target.value);setPage(1);}} style={{height:38,padding:'0 10px',border:'1px solid #e2e8f0',borderRadius:9,fontSize:12.5,background:'#f8fafc',cursor:'pointer',color:'#475569',fontFamily:'inherit'}}>
//           <option value="">All Statuses</option><option value="completed">✅ Completed</option><option value="pending">⏳ Pending</option><option value="failed">❌ Failed</option>
//         </select>
//         <select value={planF} onChange={e=>{setPlanF(e.target.value);setPage(1);}} style={{height:38,padding:'0 10px',border:'1px solid #e2e8f0',borderRadius:9,fontSize:12.5,background:'#f8fafc',cursor:'pointer',color:'#475569',fontFamily:'inherit'}}>
//           <option value="">All Plans</option><option value="basic">🟡 Basic</option><option value="pro">🔵 Pro</option><option value="business">🟣 Business</option>
//         </select>
//         {(statusF||planF||debouncedSearch)&&<button onClick={()=>{setSearch('');setStatusF('');setPlanF('');setPage(1);}} style={{height:38,padding:'0 14px',background:'#fee2e2',color:'#dc2626',border:'1px solid #fca5a5',borderRadius:8,fontSize:12,fontWeight:700,cursor:'pointer'}}>✕ Clear</button>}
//       </div>

//       {/* ERROR */}
//       {error&&<div style={{background:'#fee2e2',color:'#dc2626',border:'1px solid #fca5a5',borderRadius:9,padding:'11px 16px',marginBottom:14,fontSize:12.5,fontWeight:600,display:'flex',justifyContent:'space-between',alignItems:'center'}}><span>❌ {error}</span><button onClick={fetchData} style={{background:'none',border:'none',color:'#dc2626',cursor:'pointer',fontWeight:700,textDecoration:'underline'}}>Retry</button></div>}

//       {/* TABLE */}
//       <div style={{overflowX:'auto',border:'1px solid #e2e8f0',borderRadius:14,background:'#fff',boxShadow:'0 1px 4px #0001'}}>
//         <table style={{width:'100%',borderCollapse:'collapse',fontSize:12.5}}>
//           <thead>
//             <tr>
//               {COLS.map(col=>(
//                 <th key={col.key} style={{...TH,width:col.w}}>
//                   {col.label}
//                   {col.isNew&&<span style={{marginLeft:4,background:'#6366f1',color:'#fff',fontSize:8,fontWeight:800,borderRadius:10,padding:'1px 5px',verticalAlign:'middle'}}>NEW</span>}
//                 </th>
//               ))}
//             </tr>
//           </thead>
//           <tbody>
//             {loading?Array.from({length:7}).map((_,i)=>(
//               <tr key={i} style={{background:i%2===0?'#fff':'#fafafa'}}>
//                 {COLS.map((col,j)=>(
//                   <td key={j} style={TD}><div style={{height:13,background:'#f1f5f9',borderRadius:4,width:j===0?18:j===1?28:`${48+Math.sin(i*j)*28}%`}}/></td>
//                 ))}
//               </tr>
//             )):rows.length===0?(
//               <tr><td colSpan={COLS.length} style={{padding:'60px 20px',textAlign:'center'}}>
//                 <div style={{fontSize:46,marginBottom:12}}>📭</div>
//                 <div style={{fontSize:14,fontWeight:700,color:'#475569',marginBottom:4}}>No transactions found</div>
//                 <div style={{fontSize:12.5,color:'#94a3b8'}}>{debouncedSearch||statusF||planF?'Try adjusting your filters.':'Transactions will appear here once payments are made.'}</div>
//               </td></tr>
//             ):rows.map((row,rowIdx)=>{
//               const isEx=expandedId===row.id;
//               const sm=statusMeta(row.status);
//               const pm=planMeta(row.plan);
//               const bg=isEx?'#f0f4ff':rowIdx%2===0?'#fff':'#fafbff';
//               return(
//                 <React.Fragment key={row.id}>
//                   <tr style={{background:bg,cursor:'pointer',transition:'background 0.1s'}} onMouseEnter={e=>{if(!isEx)e.currentTarget.style.background='#f1f5ff';}} onMouseLeave={e=>{if(!isEx)e.currentTarget.style.background=bg;}} onClick={()=>setExpandedId(isEx?null:row.id)}>

//                     {/* ▼ */}
//                     <td style={{...TD,textAlign:'center',paddingLeft:6,paddingRight:6}}>
//                       <span style={{fontSize:11,color:'#94a3b8',display:'inline-block',transition:'transform 0.18s',transform:isEx?'rotate(180deg)':'none'}}>▼</span>
//                     </td>

//                     {/* # */}
//                     <td style={TD}><Mono color="#94a3b8" size={11}>#{row.id}</Mono></td>

//                     {/* User */}
//                     <td style={TD}>
//                       <div style={{display:'flex',alignItems:'center',gap:8}}>
//                         <div style={{width:30,height:30,borderRadius:'50%',background:avatarColor(row.user_email),color:'#fff',display:'flex',alignItems:'center',justifyContent:'center',fontSize:11,fontWeight:800,flexShrink:0}}>{initials(row.user_email)}</div>
//                         <div style={{minWidth:0}}>
//                           <div style={{fontSize:12,fontWeight:700,color:'#1e293b',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',maxWidth:130}}>{row.user_name}</div>
//                           <div style={{fontSize:10.5,color:'#64748b',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',maxWidth:130}}>{row.user_email}</div>
//                         </div>
//                       </div>
//                     </td>

//                     {/* Ad Name */}
//                     <td style={TD}><div style={{fontSize:12,fontWeight:600,color:'#1e293b',maxWidth:145,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}} title={row.ad_name}>{row.ad_name||<Dash/>}</div></td>

//                     {/* AD ID / Code */}
//                     <td style={TD}>
//                       <Mono color="#6366f1" size={11.5}>{row.ad_code}</Mono>
//                       {row.ad_id&&<div><Mono color="#94a3b8" size={9.5}>id #{row.ad_id}</Mono></div>}
//                     </td>

//                     {/* Media */}
//                     <td style={TD}>
//                       <div style={{display:'flex',flexDirection:'column',gap:3}}>
//                         <MediaCount icon="🖼️" count={row.ad_image_count??0} activeColor="#0ea5e9"/>
//                         <MediaCount icon="🎬" count={row.ad_video_count??0} activeColor="#7c3aed"/>
//                       </div>
//                     </td>

//                     {/* Contact */}
//                     <td style={TD} onClick={e=>e.stopPropagation()}>
//                       {nilDash(row.ad_contact)?(
//                         <div>
//                           <Mono color="#0ea5e9" size={11.5}>{row.ad_contact}</Mono>
//                           {row.ad_city&&<div style={{fontSize:10,color:'#94a3b8',marginTop:1}}>📍 {row.ad_city}</div>}
//                         </div>
//                       ):<Dash/>}
//                     </td>

//                     {/* Plan */}
//                     <td style={TD}>
//                       <Pill bg={pm.bg} color={pm.color} border={pm.border}>
//                         <span style={{width:6,height:6,borderRadius:'50%',background:pm.dot,display:'inline-block'}}/>
//                         {(row.plan||'—').toUpperCase()}
//                       </Pill>
//                     </td>

//                     {/* Amount */}
//                     <td style={TD}>
//                       <div style={{fontWeight:800,fontSize:12.5,color:'#0f172a'}}>{fmtKsh(row.amount)}</div>
//                       {row.is_discounted&&<Pill bg="#fef3c7" color="#d97706" border="#fde68a" style={{marginTop:3}}>🏷 -{row.discount_pct}%</Pill>}
//                     </td>

//                     {/* Status */}
//                     <td style={TD}><Pill bg={sm.bg} color={sm.color}>{sm.icon} {row.status}</Pill></td>

//                     {/* Txn Ref */}
//                     <td style={TD} onClick={e=>e.stopPropagation()}>
//                       <span title={row.transaction_ref} style={{display:'block',fontSize:10.5,fontWeight:700,color:'#0f172a',fontFamily:'monospace',maxWidth:118,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',cursor:'text',userSelect:'all'}}>{row.transaction_ref||'—'}</span>
//                     </td>

//                     {/* Referee — was the POSTER themselves referred by someone? */}
//                     <td style={TD}>
//                       {row.poster_is_referee?(
//                         <div>
//                           <div style={{fontSize:9.5,color:'#7c3aed',fontWeight:800,marginBottom:1,textTransform:'uppercase',letterSpacing:'0.05em'}}>via referral</div>
//                           <div style={{fontSize:10.5,fontWeight:700,color:'#6366f1',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',maxWidth:140}} title={row.poster_referred_by}>← {row.poster_referred_by}</div>
//                           {row.poster_referred_by_name&&<div style={{fontSize:10,color:'#94a3b8',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',maxWidth:140}}>{row.poster_referred_by_name}</div>}
//                         </div>
//                       ):<Dash/>}
//                     </td>

//                     {/* Referral Commission */}
//                     <td style={TD}>
//                       {row.has_referral?(
//                         <div>
//                           <div style={{fontSize:10.5,fontWeight:700,color:'#6366f1',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',maxWidth:140}} title={row.referrer_email}>↑ {row.referrer_email}</div>
//                           <div style={{fontSize:10,color:'#94a3b8',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',maxWidth:140}} title={row.referee_email}>↓ {row.referee_email}</div>
//                         </div>
//                       ):<Dash/>}
//                     </td>

//                     {/* Promotion */}
//                     <td style={TD}>
//                       {row.is_promoted?<Pill bg={promoMeta(row.promotion_type).bg} color={promoMeta(row.promotion_type).color}>🎁 {promoMeta(row.promotion_type).label}</Pill>:<Dash/>}
//                     </td>

//                     {/* Commission editor */}
//                     <td style={TD} onClick={e=>e.stopPropagation()}>
//                       <CommissionEditor row={row} onUpdated={handleRowUpdate}/>
//                     </td>

//                     {/* Date */}
//                     <td style={TD}>
//                       <div style={{fontSize:11.5,color:'#475569',whiteSpace:'nowrap'}}>{row.created_at?new Date(row.created_at).toLocaleDateString('en-KE',{day:'2-digit',month:'short',year:'numeric'}):'—'}</div>
//                       <div style={{fontSize:10,color:'#94a3b8',marginTop:1,whiteSpace:'nowrap'}}>{row.created_at?new Date(row.created_at).toLocaleTimeString('en-KE',{hour:'2-digit',minute:'2-digit'}):''}</div>
//                     </td>

//                   </tr>
//                   {isEx&&<RowDetail row={row} colCount={COLS.length}/>}
//                 </React.Fragment>
//               );
//             })}
//           </tbody>
//         </table>
//       </div>

//       {/* PAGINATION */}
//       {!loading&&totalPages>1&&(
//         <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginTop:16,flexWrap:'wrap',gap:8}}>
//           <span style={{fontSize:12,color:'#94a3b8'}}>Showing {((page-1)*PAGE_SIZE)+1}–{Math.min(page*PAGE_SIZE,totalCount)} of {totalCount.toLocaleString()}</span>
//           <div style={{display:'flex',gap:4,alignItems:'center'}}>
//             <button onClick={()=>setPage(p=>Math.max(1,p-1))} disabled={page===1} style={{width:32,height:32,border:'1px solid #e2e8f0',borderRadius:7,background:'#fff',color:'#475569',fontSize:13,fontWeight:700,cursor:page===1?'not-allowed':'pointer'}}>‹</button>
//             {pageNums().map((n,i)=>n==='…'?<span key={`e${i}`} style={{padding:'0 4px',color:'#94a3b8',fontSize:13}}>…</span>:<button key={n} onClick={()=>setPage(n)} style={{width:32,height:32,border:`1px solid ${page===n?'#6366f1':'#e2e8f0'}`,borderRadius:7,background:page===n?'#6366f1':'#fff',color:page===n?'#fff':'#475569',fontSize:12.5,fontWeight:700,cursor:page===n?'default':'pointer'}}>{n}</button>)}
//             <button onClick={()=>setPage(p=>Math.min(totalPages,p+1))} disabled={page===totalPages} style={{width:32,height:32,border:'1px solid #e2e8f0',borderRadius:7,background:'#fff',color:'#475569',fontSize:13,fontWeight:700,cursor:page===totalPages?'not-allowed':'pointer'}}>›</button>
//           </div>
//         </div>
//       )}

//     </div>
//   );
// }

// export default TransactionsTable;



